import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { minusIcon, plusIcon, successIcon } from '@justgiving/icons';
import Loading from '@justgiving/loading';
import SVG from '@justgiving/svg';
import cx from 'classnames';

import { CheckListDataQuery } from '../../../types/__generated__';
import { useFullPageQuery } from '../../hooks';
import { useIsMounted } from '../../hooks/useIsMounted';
import useVideoStory from '../../hooks/videoStory/useVideoStory';
import { buildViewData } from '../../utils/buildViewData';
import { formatLargeValues } from '../../utils/formatLargeValues';
import { filterByTypename } from '../../utils/graphql';
import { getMeasurementSystemFromSettings } from '../../utils/measurementSystem';
import { onlyImages } from '../../utils/typeGuards';
import { ActivityFilters } from '../ActivityTotals/filters';
import Button from '../Button/Button';
import { IconButton } from '../IconButton/IconButton';
import { Tag } from '../Tag/Tag';
import { VideoStoryModal } from '../VideoStoryModal/VideoStoryModal';
import { ClapperIcon } from './ClapperIcon/ClapperIcon';
import Checklist from './VideoStoryCheckList.gql';

import styles from './VideoStoryCheckList.scss';

export interface ListItem {
  id: string;
  value?: React.ReactNode;
  checked: boolean;
}

const VideoStoryCheckList: FunctionComponent = () => {
  const { t } = useTranslation('common', { useSuspense: false });
  const isMounted = useIsMounted();
  const [
    createVideoStoryEntry,
    mutationResults,
    loading,
    videoStory,
  ] = useVideoStory();
  const [showModal, setShowModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(videoStory?.status === 'done');
  const { data, loading: dataLoading } = useFullPageQuery<CheckListDataQuery>(
    Checklist,
    {
      notifyOnNetworkStatusChange: true,
      context: {
        credentials: 'include',
      },
    },
  );

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  if (!isMounted) {
    return null;
  }
  if (dataLoading) {
    return <Loading />;
  }
  if (!data) {
    return null;
  }
  const { page } = data;

  const hasVideo =
    videoStory?.status === 'done' && videoStory?.videoUrl != null;

  const settings = page?.application?.settings;
  const measurementSystem = getMeasurementSystemFromSettings(settings);

  const hasFitnessActivity =
    (data?.page?.timeline?.nodes.filter(node => node.type === 'FITNESS')
      .length || 0) >= 1;

  const checkItems: ListItem[] | undefined[] = [
    {
      id: 'title',
      value: t('checkItem.title', 'Page summary'),
      checked: page?.summary !== null || page.summary !== undefined,
    },
    {
      id: '3+imgages',
      value: t('checkItem.story', 'At least 3 images'),
      checked: (data?.page?.gallery?.nodes?.length || 0) >= 2,
    },
    {
      id: 'donation',
      value: t('checkItem.story', 'At least 1 donation'),
      checked: (data?.page?.donations?.totalCount || 0) >= 1,
    },
    {
      id: 'timeline',
      value: t('checkItem.story', 'At least 1 timeline event'),
      checked: (data?.page?.timeline?.totalCount || 0) >= 1,
    },
    measurementSystem && {
      id: 'fitness',
      value: t('checkItem.story', 'At least 1 fitness activity'),
      checked: hasFitnessActivity,
    },
  ];

  const { activityTotalsMap } = buildViewData(
    page?.totals?.timeline || [],
    measurementSystem,
    ActivityFilters.all.key,
    () => {},
  );

  const total = activityTotalsMap[ActivityFilters.all.key];
  const primaryMeasurement = total?.['distance'];

  const heading =
    videoStory?.status === 'done'
      ? t('videoSection.heading.rendered', 'View your video story!')
      : t('videoSection.heading', 'Create your video story!');

  const description =
    videoStory?.status === 'done'
      ? t(
          'videoSection.description.rendered',
          'Your video is ready to watch, you can recreate your video when you add more images or have more donations',
        )
      : t(
          'videoSection.description',
          'When your page will meet the requirements we will prepare a custom video for you',
        );

  const isDisabled = !checkItems.every(v => v.checked === true);

  const gallery =
    page?.gallery?.nodes.filter(onlyImages).map(image => {
      return { url: image.url, caption: image.caption ?? undefined };
    }) ?? [];
  const galleryUrls = gallery.map(image => image.url);

  const coverUrl = page?.cover?.__typename === 'ImageMedia' && page?.cover?.url;
  const formatedTotals = formatLargeValues(
    primaryMeasurement?.value ?? 0,
    primaryMeasurement?.unit ?? 'METRES',
  );
  const beneficiaries = page?.relationships?.beneficiaries;
  const charities = beneficiaries?.nodes
    ? filterByTypename(beneficiaries.nodes, 'Charity')
    : null;

  const input = {
    userName: page?.owner?.name?.split(' ')[0],
    pageTitle: page?.title,
    pageStatement: page?.summary,
    pageGuid: page?.legacyId,
    donationTotal: page?.donationSummary?.totalAmount?.value / 100,
    currency: page?.donationSummary?.totalAmount?.currencyCode,
    imageUrls: [coverUrl, ...galleryUrls],
    charityLogoUrl: charities![0].logo,
    fitness: measurementSystem
      ? {
          total: formatedTotals.value,
          metric: formatedTotals.unit.toLowerCase(),
        }
      : {},
  };

  return (
    <div
      className={cx(
        styles.videoSection,
        isExpanded && styles['videoSection--expanded'],
      )}
    >
      <div className={styles.headingContainer}>
        <span>
          <Tag>{t('common.new')}</Tag>
          <h1 className={`jg-h4 jg-display-ib ${styles.heading}`}>{heading}</h1>
          {/* need to include status */}
          <span
            style={{
              width: '20px',
              display: 'inline-block',
              position: 'relative',
              top: ' -4px',
              marginLeft: '5px',
            }}
          >
            <ClapperIcon />
          </span>
        </span>

        <IconButton
          aria-label={isExpanded ? t('common.collapse') : t('common.expand')}
          onClick={toggleExpanded}
        >
          <SVG
            markup={isExpanded ? minusIcon : plusIcon}
            className={styles.icon}
          />
        </IconButton>
      </div>
      {isExpanded && (
        <>
          <p className="jg-text--large">{description}</p>
          {!videoStory?.videoUrl ? (
            <ul className="jg-list--unstyled">
              {checkItems
                .filter(item => item !== undefined)
                .map(({ id, value, checked }) => (
                  <li key={id}>
                    <p
                      className={cx(
                        styles.checklistItem,
                        checked && styles['checklistItem--checked'],
                      )}
                    >
                      <SVG
                        markup={successIcon}
                        className={checked ? styles.success : styles.default}
                      />

                      {value}
                    </p>
                  </li>
                ))}
            </ul>
          ) : null}

          {videoStory?.status === 'processing' ? (
            <div>We're just rendering your video now... </div>
          ) : (
            <Button
              type="button"
              onClick={
                hasVideo
                  ? () => setShowModal(true)
                  : () => createVideoStoryEntry(input)
              }
              fullWidth
              disabled={isDisabled}
              className={styles.button}
            >
              {hasVideo ? 'View your Video' : 'Generate Video'}
            </Button>
          )}
          {videoStory?.status === 'done' && (
            <Button
              type="button"
              onClick={() => createVideoStoryEntry(input)}
              fullWidth
              disabled={isDisabled}
              className={styles.buttonGrey}
            >
              Generate New Video
            </Button>
          )}
        </>
      )}
      {hasVideo && showModal && (
        <VideoStoryModal
          setShowModal={setShowModal}
          videoUrl={videoStory?.videoUrl!}
        />
      )}
    </div>
  );
};

export default VideoStoryCheckList;
